import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { SnackbarService } from 'app/core/snackbars/snackbar.service';
import { finalize, Observable } from 'rxjs';
import { NotificationsService } from './notifications.service';
import {
    Notification,
    NOTIFICATION_LINK_MAX_LENGTH,
    NOTIFICATION_MESSAGE_MAX_LENGTH,
    NotificationColleague,
    NotificationLab,
    SendNotificationResult,
} from './notifications.types';

export interface ComposeNotificationDialogData {
    // Router URL of the page the dialog was opened from, offered as the notification's link.
    currentUrl?: string;
    // Reply to this received notification's sender instead of picking recipients.
    replyTo?: Notification;
}

@Component({
    selector: 'compose-notification-dialog',
    standalone: true,
    imports: [FormsModule, MatDialogModule, MatButtonModule, MatCheckboxModule, MatFormFieldModule, MatIconModule, MatInputModule, MatSelectModule],
    template: `
        <h2 mat-dialog-title>{{ data.replyTo ? 'Reply to ' + data.replyTo.senderName : 'New notification' }}</h2>
        <mat-dialog-content>
            <div class="flex flex-col gap-2 pt-2 min-w-72 sm:min-w-120">
                @if (data.replyTo; as replyTo) {
                    <div class="pcs-card p-3">
                        <div class="text-secondary text-sm">{{ replyTo.senderName }} wrote:</div>
                        <div class="mt-1 line-clamp-4 whitespace-pre-line break-words">{{ replyTo.message }}</div>
                    </div>
                } @else {
                    @if (isSupportUser) {
                        <mat-form-field class="w-full">
                            <mat-label>Lab</mat-label>
                            <mat-select [(ngModel)]="labId" (selectionChange)="loadRecipients()" [disabled]="isLoadingLabs" [placeholder]="isLoadingLabs ? 'Loading labs...' : 'Choose a lab'">
                                @for (lab of labs; track lab.labId) {
                                    <mat-option [value]="lab.labId">{{ lab.labName }}</mat-option>
                                }
                            </mat-select>
                        </mat-form-field>
                    }

                    <mat-form-field class="w-full">
                        <mat-label>To</mat-label>
                        <mat-select multiple [(ngModel)]="recipientUserIds" [disabled]="wholeLab || isLoading || needsLab" [placeholder]="isLoading ? 'Loading colleagues...' : ''">
                            @if (labColleagues.length) {
                                <mat-optgroup [label]="labGroupLabel">
                                    @for (colleague of labColleagues; track colleague.userId) {
                                        <mat-option [value]="colleague.userId">
                                            {{ colleague.name }} {{ colleague.surname }}
                                            <span class="text-secondary text-sm">· {{ colleague.email }}</span>
                                        </mat-option>
                                    }
                                </mat-optgroup>
                            }
                            @if (supportColleagues.length) {
                                <mat-optgroup label="Dankospark support">
                                    @for (colleague of supportColleagues; track colleague.userId) {
                                        <mat-option [value]="colleague.userId">
                                            {{ colleague.name }} {{ colleague.surname }}
                                            <span class="text-secondary text-sm">· {{ colleague.email }}</span>
                                        </mat-option>
                                    }
                                </mat-optgroup>
                            }
                        </mat-select>
                        @if (needsLab) {
                            <mat-hint>Choose a lab first.</mat-hint>
                        } @else if (!isLoading && labColleagues.length === 0) {
                            <mat-hint>There's no one else in this lab yet.</mat-hint>
                        }
                    </mat-form-field>

                    <mat-checkbox [(ngModel)]="wholeLab" [disabled]="isLoading || labColleagues.length === 0">
                        Everyone in {{ isSupportUser ? (labName ?? 'the lab') : 'my lab' }} ({{ labColleagues.length }})
                    </mat-checkbox>
                }

                <mat-form-field class="mt-2 w-full">
                    <mat-label>Message</mat-label>
                    <textarea matInput rows="4" [(ngModel)]="message" [maxlength]="maxLength"></textarea>
                    <mat-hint align="end">{{ message.length }} / {{ maxLength }}</mat-hint>
                </mat-form-field>

                @if (canLink) {
                    <mat-checkbox [(ngModel)]="includeLink">
                        Link to this page <span class="text-secondary">({{ data.currentUrl }})</span>
                    </mat-checkbox>
                }
            </div>
        </mat-dialog-content>
        <mat-dialog-actions align="end">
            <button mat-stroked-button class="pcs-btn-default" mat-dialog-close>Cancel</button>
            <button mat-flat-button class="pcs-btn-success" [disabled]="!canSend" (click)="send()">
                <mat-icon class="icon-size-4 mr-1" [svgIcon]="'heroicons_outline:paper-airplane'"></mat-icon>
                {{ data.replyTo ? 'Reply' : 'Send' }}
            </button>
        </mat-dialog-actions>
    `,
})
export class ComposeNotificationDialogComponent implements OnInit {
    readonly maxLength = NOTIFICATION_MESSAGE_MAX_LENGTH;

    // Dankospark support staff write to a lab they choose; everyone else writes as their own lab.
    readonly isSupportUser: boolean;
    labs: NotificationLab[] = [];
    labId: string | null = null;
    isLoadingLabs = false;

    // "Everyone in lab" goes to labColleagues only; Dankospark support has to be picked by name.
    labColleagues: NotificationColleague[] = [];
    supportColleagues: NotificationColleague[] = [];
    recipientUserIds: string[] = [];
    wholeLab = false;
    message = '';
    includeLink = false;
    isLoading = false;
    isSending = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: ComposeNotificationDialogData,
        private _dialogRef: MatDialogRef<ComposeNotificationDialogComponent, boolean>,
        private _notificationsService: NotificationsService,
        private _snackbarService: SnackbarService,
    ) {
        this.isSupportUser = _notificationsService.isSupportUser;
    }

    ngOnInit(): void {
        // A reply already has its recipient
        if (this.data.replyTo) {
            return;
        }

        if (!this.isSupportUser) {
            this.loadRecipients();
            return;
        }

        this.isLoadingLabs = true;
        this._notificationsService
            .getLabs()
            .pipe(finalize(() => (this.isLoadingLabs = false)))
            .subscribe((labs) => {
                this.labs = labs;
                this.labId = labs.find((lab) => lab.isMine)?.labId ?? null;
                if (this.labId) {
                    this.loadRecipients();
                }
            });
    }

    loadRecipients(): void {
        this.recipientUserIds = [];
        this.wholeLab = false;
        this.labColleagues = [];
        this.supportColleagues = [];

        this.isLoading = true;
        this._notificationsService
            .getRecipients(this.isSupportUser ? this.labId : undefined)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe((colleagues) => {
                this.labColleagues = colleagues.filter((colleague) => colleague.inLab);
                this.supportColleagues = colleagues.filter((colleague) => !colleague.inLab);
            });
    }

    // A Dankospark user hasn't picked the lab to write to yet.
    get needsLab(): boolean {
        return this.isSupportUser && !this.labId;
    }

    get labName(): string | null {
        return this.labs.find((lab) => lab.labId === this.labId)?.labName ?? null;
    }

    get labGroupLabel(): string {
        return this.isSupportUser ? (this.labName ?? 'Lab') : 'My lab';
    }

    // Linking to the Notifications page itself would be pointless.
    get canLink(): boolean {
        const url = this.data?.currentUrl ?? '';
        return url.startsWith('/') && !url.startsWith('/notifications') && url.length <= NOTIFICATION_LINK_MAX_LENGTH;
    }

    get canSend(): boolean {
        const hasRecipients = this.data.replyTo
            || (!this.needsLab && (this.wholeLab ? this.labColleagues.length > 0 : this.recipientUserIds.length > 0));
        return !this.isSending && !!hasRecipients && this.message.trim().length > 0;
    }

    send(): void {
        const message = this.message.trim();
        const link = this.includeLink && this.canLink ? this.data.currentUrl : null;

        const request: Observable<SendNotificationResult> = this.data.replyTo
            ? this._notificationsService.reply(this.data.replyTo, message, link)
            : this._notificationsService.send({
                labId: this.isSupportUser ? this.labId : null,
                recipientUserIds: this.wholeLab ? [] : this.recipientUserIds,
                wholeLab: this.wholeLab,
                message,
                link,
            });

        this.isSending = true;
        request
            .pipe(finalize(() => (this.isSending = false)))
            .subscribe((result) => {
                if (this.data.replyTo) {
                    this._snackbarService.success(`Reply sent to ${this.data.replyTo.senderName}.`);
                } else {
                    const people = result.recipientCount === 1 ? 'person' : 'people';
                    this._snackbarService.success(`Notification sent to ${result.recipientCount} ${people}.`);
                }
                this._dialogRef.close(true);
            });
    }
}
